import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Paper,
  IconButton,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TableContainer,
  InputAdornment
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from "@mui/lab";
import {
  Close,
  LocalHospital,
  Science,
  Medication,
  Assignment,
  CalendarToday,
  Search as SearchIcon,
  Clear as ClearIcon
} from "@mui/icons-material";
import { getPatientTimeline_medicalHistorydb, getPatientVisitDetailsdb} from "../BaseURL";
import axios from "axios";

const MedicalHistoryDialog = ({ open, onClose, patientData }) => {

  const [medicalEvents, setMedicalEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Filtering Logic - added safety check
  const filteredEvents = useMemo(() => {
    if (!medicalEvents || !Array.isArray(medicalEvents)) {
      return [];
    }
    
    return medicalEvents.filter((ev) => {
      const matchesSearch =
        (ev.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ev.type || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ev.doctor || "").toLowerCase().includes(searchTerm.toLowerCase());

      const date = new Date(ev.date);
      const afterStart = startDate ? date >= new Date(startDate) : true;
      const beforeEnd = endDate ? date <= new Date(endDate) : true;

      return matchesSearch && afterStart && beforeEnd;
    });
  }, [searchTerm, startDate, endDate, medicalEvents]);

  // Load timeline list
  const loadTimeline = async () => {
    if (!patientData) return;

    try {
      const response = await axios.get(getPatientTimeline_medicalHistorydb, {
        params: { patientId: patientData.id }
      });

      if (response.data.success) {
        setMedicalEvents(response.data.events || []);
       // console.log(response.data.events);
      }
    } catch (error) {
      console.error("Error loading timeline:", error);
      setMedicalEvents([]); // Set empty array on error
    }
  };

  // Load visit details when timeline item is clicked
  const loadVisitDetails = async (event) => {
   
    setSelectedEvent(event);
    setLoadingDetails(true);
    setEventDetails(null);

    try {
      const response = await axios.get(getPatientVisitDetailsdb, {
        params: { 
          patientId: patientData.id,
          visitDate: event.date
        }
      });

      if (response.data.success) {
        setEventDetails(response.data.details);
        console.log(response.data.details)
      }
    } catch (error) {
      console.error("Error loading visit details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (open && patientData) {
      loadTimeline();
    }
  }, [open, patientData]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedEvent(null);
      setEventDetails(null);
      setSearchTerm("");
      setStartDate("");
      setEndDate("");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh"
        }
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: "linear-gradient(135deg, #1A9B7F 0%, #15826A 100%)",
            px: 3, py: 2.25,
            color: "white",
            display: "flex", alignItems: "center", gap: 2,
          }}
        >
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.28)",
            backdropFilter: "blur(10px)",
            fontWeight: 800, fontSize: "1.15rem",
            flexShrink: 0,
          }}>
            {(patientData?.fullName || "?")
              .split(/\s+/).filter(Boolean).slice(0, 2)
              .map(n => n[0]).join("").toUpperCase()}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{
              fontSize: "0.7rem", fontWeight: 700, letterSpacing: "1.4px",
              textTransform: "uppercase", opacity: 0.85, lineHeight: 1,
            }}>
              Patient Record
            </Typography>
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, mt: 0.4, lineHeight: 1.2 }}>
              Medical History
            </Typography>
            {patientData && (
              <Typography sx={{ fontSize: "0.85rem", opacity: 0.9, mt: 0.25 }}>
                {patientData.fullName} · ID {patientData.id}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "white",
              bgcolor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
              transition: "background-color 0.2s ease",
              "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          p: 0, 
          display: "flex", 
          height: "100%",
          flexDirection: { xs: "column", md: "row" }
        }}
      >

        {/* LEFT SIDE - Timeline */}
        <Box
          sx={{
            width: { xs: "100%", md: "40%" },
            background: "linear-gradient(160deg, #1A9B7F 0%, #15826A 100%)",
            color: "white",
            overflowY: "auto",
            p: { xs: 2, md: 2.5 },
            maxHeight: { xs: "40vh", md: "100%" }
          }}
        >
          {/* Filter panel */}
          <Box sx={{
            mb: 2.5,
            p: 1.75,
            borderRadius: 2.5,
            bgcolor: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.18)",
            backdropFilter: "blur(8px)",
          }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
              <Typography sx={{
                fontSize: "0.66rem", fontWeight: 700,
                letterSpacing: "1.4px", textTransform: "uppercase",
                color: "rgba(255,255,255,0.9)",
              }}>
                Filter Visits
              </Typography>
              <Box sx={{
                bgcolor: "rgba(255,255,255,0.22)",
                border: "1px solid rgba(255,255,255,0.3)",
                px: 1, py: 0.25,
                borderRadius: 1,
                fontSize: "0.7rem", fontWeight: 700,
                color: "white",
              }}>
                {filteredEvents.length} {filteredEvents.length === 1 ? "visit" : "visits"}
              </Box>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search visits, diagnoses, doctors…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")} sx={{ color: "#94a3b8" }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.25,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  borderRadius: 1.75,
                  "& fieldset": { border: "none" },
                  fontSize: "0.86rem",
                },
              }}
            />

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography sx={{
                  fontSize: "0.64rem", fontWeight: 700, color: "rgba(255,255,255,0.85)",
                  textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, ml: 0.25,
                }}>
                  From
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white", borderRadius: 1.75,
                      "& fieldset": { border: "none" },
                      fontSize: "0.82rem",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{
                  fontSize: "0.64rem", fontWeight: 700, color: "rgba(255,255,255,0.85)",
                  textTransform: "uppercase", letterSpacing: "0.6px", mb: 0.5, ml: 0.25,
                }}>
                  To
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white", borderRadius: 1.75,
                      "& fieldset": { border: "none" },
                      fontSize: "0.82rem",
                    },
                  }}
                />
              </Grid>
            </Grid>

            {(searchTerm || startDate || endDate) && (
              <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  onClick={() => { setSearchTerm(""); setStartDate(""); setEndDate(""); }}
                  sx={{
                    textTransform: "none", fontWeight: 600, fontSize: "0.72rem",
                    color: "white", py: 0.25, px: 1, minWidth: 0,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                  }}
                >
                  Clear filters
                </Button>
              </Box>
            )}
          </Box>

          {/* Timeline */}
          <Timeline position="right">
            {filteredEvents.length === 0 ? (
              <Typography sx={{ textAlign: "center", mt: 5, opacity: 0.8 }}>
                No medical records found.
              </Typography>
            ) : (
              filteredEvents.map((event, index) => (
                <TimelineItem key={index}>
                  <TimelineOppositeContent sx={{ m: "auto 0", flex: 0.3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {event.date}
                    </Typography>
                  </TimelineOppositeContent>

                  <TimelineSeparator>
                    <TimelineConnector
                      sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
                    />
                    <TimelineDot color={event.color || "primary"}>
                      <LocalHospital />
                    </TimelineDot>
                    <TimelineConnector
                      sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
                    />
                  </TimelineSeparator>

                  <TimelineContent sx={{ py: 2, px: 2 }}>
                    <Paper
                      elevation={
                        selectedEvent?.date === event.date ? 12 : 4
                      }
                      onClick={() => loadVisitDetails(event)}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        borderRadius: 2,
                        backgroundColor:
                          selectedEvent?.date === event.date
                            ? "rgba(255,255,255,1)"
                            : "rgba(255,255,255,0.9)",
                        border:
                          selectedEvent?.date === event.date
                            ? "3px solid #667eea"
                            : "2px solid transparent",
                        transition: "0.25s",
                        "&:hover": { transform: "translateY(-4px)" }
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        {event.title}
                      </Typography>
                      <Chip
                        label={event.type}
                        size="small"
                        sx={{ mt: 1, fontSize: "0.7rem" }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={1}
                      >
                        Dr. {event.doctor}
                      </Typography>
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              ))
            )}
          </Timeline>
        </Box>

        {/* RIGHT SIDE - Detailed View */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 4,
            backgroundColor: "#f5f5f5",
            maxHeight: { xs: "60vh", md: "100%" }
          }}
        >
          {loadingDetails ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress />
            </Box>
          ) : eventDetails ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3
                }}
              >
                <Box>
                  <Typography variant="h4" fontWeight={600} gutterBottom>
                    {selectedEvent.title}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {selectedEvent.date}
                  </Typography>
                </Box>
                <Chip
                  label={selectedEvent.type}
                  color={selectedEvent.color || "primary"}
                  sx={{ fontSize: "1rem", py: 2.5, px: 1 }}
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

               {/* Patient Vitals */}
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Vitals
                  </Typography>

                 <Table size="small">
              <TableBody>
                {[
                  { label: 'Blood Pressure', value: eventDetails.vitals?.bp, unit: 'mmHg', color: '#ef4444' },
                  { label: 'Temperature', value: eventDetails.vitals?.temp, unit: '°C', color: '#f59e0b' },
                  { label: 'Pulse', value: eventDetails.vitals?.pulse, unit: 'bpm', color: '#ec4899' },
                  { label: 'Resp. Rate', value: eventDetails.vitals?.resp, unit: '/min', color: '#06b6d4' },
                  { label: 'SpO₂', value: eventDetails.vitals?.spo2, unit: '%', color: '#3b82f6' },
                  { label: 'Weight', value: eventDetails.vitals?.weight, unit: 'kg', color: '#8b5cf6' },
                  { label: 'Height', value: eventDetails.vitals?.height, unit: 'cm', color: '#10b981' },
                  { label: 'BMI', value: eventDetails.vitals?.bmi, unit: '', color: '#6366f1' },
                ].map((item, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:last-child td': { border: 0 },
                      '&:hover': { 
                        bgcolor: 'action.hover',
                        '& .vital-indicator': {
                          transform: 'scale(1.1)',
                          transition: 'transform 0.2s'
                        }
                      }
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.secondary',
                        width: '40%',
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        border: 0
                      }}
                    >
                      <Box
                        className="vital-indicator"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          flexShrink: 0,
                          transition: 'transform 0.2s'
                        }}
                      />
                      {item.label}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        color: 'text.primary',
                        py: 1.5,
                        border: 0
                      }}
                    >
                      <Box component="span" sx={{ color: item.color, fontWeight: 600 }}>
                        {item.value && item.value.toString().trim() 
                          ? item.value
                          : '—'}
                      </Box>
                      {item.value && item.value.toString().trim() && item.unit && (
                        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5, fontSize: '0.875rem' }}>
                          {item.unit}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

                </CardContent>
              </Card>

              {[
                { 
                  title: 'Complaint', 
                  content: eventDetails.complaint,
                  icon: '💬',
                  color: '#ef4444'
                },
                { 
                  title: 'History', 
                  content: eventDetails.history,
                  icon: '📋',
                  color: '#f59e0b'
                },
                { 
                  title: 'Physical Examination', 
                  content: eventDetails.physicalExam,
                  icon: '🩺',
                  color: '#3b82f6'
                },
                { 
                  title: 'Other Documentation', 
                  content: eventDetails.otherDocumentation,
                  icon: '📄',
                  color: '#8b5cf6'
                }
              ]
                .filter(section => section.content && section.content.trim() !== '')
                .map((section, index) => (
                  <Card 
                    key={index}
                    sx={{ 
                      mb: 3, 
                      boxShadow: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        boxShadow: 4,
                        borderColor: section.color,
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: `${section.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.25rem'
                          }}
                        >
                          {section.icon}
                        </Box>
                        <Typography 
                          variant="h6" 
                          fontWeight={600}
                          sx={{ 
                            color: 'text.primary',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          {section.title}
                        </Typography>
                      </Box>
                      
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.7,
                          pl: 7
                        }}
                      >
                        {section.content}
                      </Typography>
                    </CardContent>
                  </Card>
                ))
              }
        

              {[
                { 
                  title: 'Diagnosis', 
                  content: eventDetails.diagnosis,
                  icon: '🔬',
                  color: '#10b981'
                },
                { 
                  title: 'Procedure', 
                  content: eventDetails.Procedure_,
                  icon: '⚕️',
                  color: '#06b6d4'
                }
              ].map((section, index) => 
                section.content && section.content.trim() !== '' ? (
                  <Card 
                    key={index}
                    sx={{ 
                      mb: 3, 
                      boxShadow: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        boxShadow: 4,
                        borderColor: section.color,
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: `${section.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.25rem'
                          }}
                        >
                          {section.icon}
                        </Box>
                        <Typography 
                          variant="h6" 
                          fontWeight={600}
                          sx={{ color: 'text.primary' }}
                        >
                          {section.title}
                        </Typography>
                      </Box>
                      
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.7,
                          pl: 7
                        }}
                      >
                        {section.content}
                      </Typography>
                    </CardContent>
                  </Card>
                ) : null
              )}

               {/* Lab Results */}
             {eventDetails.lab && Object.keys(eventDetails.lab).length > 0 && (
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Laboratory Results
                  </Typography>

                  {Object.entries(eventDetails.lab).map(([group, tests]) => (
                    <Box key={group} sx={{ mb: 4 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ mb: 1, color: "primary.main" }}
                      >
                        {group}
                      </Typography>

                      <Table size="small">
                        {/* ---------- TABLE HEADER ---------- */}
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f1f5fb" }}>
                            <TableCell sx={{ fontWeight: 700 }}>Test</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Range</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Flag</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Comments</TableCell>
                          </TableRow>
                        </TableHead>

                        {/* ---------- TABLE BODY ---------- */}
                        <TableBody>
                          {tests.map((t, idx) => (
                            <TableRow key={idx}>
                              {/* Test Name */}
                              <TableCell sx={{ fontWeight: 600, width: "20%" }}>
                                {t.name}
                              </TableCell>

                              {/* Value + Unit */}
                              <TableCell sx={{ width: "20%" }}>
                                {t.value} {t.unit}
                              </TableCell>

                              {/* Reference Range */}
                              <TableCell sx={{ width: "20%", fontStyle: "italic" }}>
                                {t.refRange || "—"}
                              </TableCell>

                              {/* Flag With Colors */}
                              <TableCell
                                sx={{
                                  width: "15%",
                                  fontWeight: 700,
                                  color:
                                    t.flag === "HIGH"
                                      ? "red"
                                      : t.flag === "LOW"
                                      ? "orange"
                                      : t.flag === "CRITICAL"
                                      ? "darkred"
                                      : t.flag
                                      ? "green"
                                      : "text.secondary"
                                }}
                              >
                                {t.flag || ""}
                              </TableCell>

                              {/* Comments */}
                              <TableCell sx={{ width: "25%" }}>
                                {t.comments || t.otherComment || ""}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {/* Technician */}
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 1, opacity: 0.8 }}
                      >
                         Technician: {tests[0].technician || "—"} | Date: {tests[0].date}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}


             {/* Scan Results */}
            {eventDetails.scan && Object.keys(eventDetails.scan).length > 0 && (
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Scan Results
                </Typography>

                {Object.entries(eventDetails.scan).map(([scanType, items]) => (
                  <Box key={scanType} sx={{ mb: 4 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      {scanType}
                    </Typography>

                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f1f5fb" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Findings</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Flag</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Comments</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {items.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: 600, width: "20%" }}>
                              {row.para}
                            </TableCell>

                            <TableCell sx={{ width: "55%" }}>
                              {row.finding}
                            </TableCell>

                            <TableCell
                              sx={{
                                width: "10%",
                                fontWeight: 700,
                                color:
                                  row.flag === "ABNORMAL"
                                    ? "red"
                                    : row.flag
                                    ? "orange"
                                    : "text.secondary"
                              }}
                            >
                              {row.flag || ""}
                            </TableCell>

                            <TableCell sx={{ width: "15%" }}>
                              —
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 1, opacity: 0.8 }}
                    >
                      Technician: {items[0].technician || "—"} | Date: {items[0].date}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
            )}


              {/* MEDICATION HISTORY */}
            {eventDetails.medications && eventDetails.medications.length > 0 && (
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Medications
                  </Typography>

                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      maxHeight: eventDetails.medications.length > 5 ? "320px" : "auto",
                      overflowY:
                        eventDetails.medications.length > 5 ? "auto" : "visible",
                      "& .MuiTableHead-root": {
                        position:
                          eventDetails.medications.length > 5 ? "sticky" : "static",
                        top: 0,
                        zIndex: 1,
                        bgcolor: "grey.50"
                      }
                    }}
                  >
                    <Table stickyHeader={eventDetails.medications.length > 5}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Medication</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">
                            Quantity
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">
                            Prescribed By
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">
                            Served By
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {eventDetails.medications.map((med, index) => (
                          <TableRow
                            key={index}
                            hover
                            sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {med.medication}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">{med.route}</Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">{med.frequency}</Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">{med.days}</Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={600}>
                                {med.quantity}
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Typography variant="body2">
                                {med.doctor || med.prescribedBy}
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Typography variant="body2">{med.prescribedBy}</Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Chip
                                label={med.status}
                                size="small"
                                color={
                                  med.status === "served"
                                    ? "success"
                                    : med.status === "pending"
                                    ? "warning"
                                    : "default"
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

             {/* Attending Physician */}
             <Card 
            sx={{ 
              boxShadow: 2,
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                boxShadow: 4,
                borderColor: '#06b6d4',
                transition: 'all 0.3s ease'
              }
            }}
          >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#06b6d415',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem'
              }}
            >
              👨‍⚕️
            </Box>
            <Typography 
              variant="h6" 
              fontWeight={600}
              sx={{ color: 'text.primary' }}
            >
              Attending Physician
            </Typography>
          </Box>
          
          <Typography 
            variant="body1"
            sx={{ 
              color: '#06b6d4',
              fontWeight: 500,
              pl: 7,
              fontSize: '1.1rem'
            }}
          >
            Dr. {selectedEvent.doctor}
          </Typography>
        </CardContent>
            </Card>
            </>
          ) : (
            <Box sx={{
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              textAlign: "center", height: "100%", px: 4,
            }}>
              <Box sx={{
                width: 88, height: 88, borderRadius: "50%",
                background: "linear-gradient(135deg, #d1fae5 0%, #ccfbf1 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                mb: 2.5,
                boxShadow: "inset 0 2px 8px rgba(15,23,42,0.06)",
              }}>
                <CalendarToday sx={{ fontSize: 38, color: "#1A9B7F" }} />
              </Box>
              <Typography sx={{ color: "#0f172a", fontWeight: 700, fontSize: "1.05rem" }}>
                Select a visit
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: "0.88rem", mt: 0.75, maxWidth: 360 }}>
                Pick any record from the timeline on the left to see vitals, diagnoses, labs, scans, and medications for that visit.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3, py: 2,
        bgcolor: "#f8fafc",
        borderTop: "1px solid #e5e9ef",
        gap: 1,
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none", fontWeight: 600, borderRadius: 1.75,
            px: 2.5, borderColor: "#cbd5e1", color: "#475569",
            "&:hover": { borderColor: "#94a3b8", bgcolor: "rgba(148,163,184,0.06)" },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          sx={{
            textTransform: "none", fontWeight: 700, borderRadius: 1.75,
            px: 3, boxShadow: "none",
            background: "linear-gradient(135deg, #1A9B7F 0%, #15826A 100%)",
            "&:hover": { boxShadow: "0 4px 12px rgba(26,155,127,0.4)" },
          }}
        >
          Print History
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalHistoryDialog;